import { useCallback, useEffect, useRef, useState } from 'react'

import { nextDelay } from '@/app/lib/quant/backoff'
import { mergeLiveSession } from '@/app/hooks/useQuantLiveSessions'
import { observeLiveSession } from '@/app/lib/quant/liveObserver'
import { isActive } from '@/app/lib/quant/liveView'
import { quantApi } from '@/app/lib/quant/quantApi'
import { isLiveGone, quantErrorMessage } from '@/app/lib/quant/quantErrors'
import type { LiveSession } from '@/app/lib/quant/types'
import { useSignalLog } from '@/app/lib/signalLog'

export type QuantLiveSessionState =
  | { status: 'loading' }
  /** `error` is set while the latest read failed; the last good session stays on screen. */
  | { status: 'ready'; session: LiveSession; receivedAt: number; error?: string }
  | { status: 'not_found' }
  | { status: 'error'; message: string }

export const SESSION_POLL_MS = 5000
export const SESSION_STOPPING_POLL_MS = 2000

export interface QuantLiveSessionHandle {
  /** True while the held session is not ended: dependent readers (events, equity, log) poll only then. */
  active: boolean
  /** True once the session is ended and its one final read is done: nothing more is read for it. */
  finalDone: boolean
  /** Reads again now (Retry, or after a refused stop or delete). */
  reload: () => void
  /** Puts a fresher answer (for example the stop answer) in place at once. */
  merge: (session: LiveSession) => void
}

type Phase = 'poll' | 'final' | 'done'

/**
 * Spec 041: one live session, read every 5 s (2 s while `stopping`). A first answer
 * that is already ended is the final read and polling stops. An answer that turns
 * ended after being active triggers exactly one more read at once (the final read),
 * then stops. A 404 ends polling as `not_found`. A failed read keeps the held session
 * (`error` beside it) and backs off 2, 4, 8, 15 s. Nothing is requested while the tab
 * is hidden and a read happens at once when it becomes visible. Every applied answer
 * feeds the terminal-line observer and the list store. Key the caller by session id.
 */
export function useQuantLiveSession(id: string): QuantLiveSessionState & QuantLiveSessionHandle {
  const [state, setState] = useState<QuantLiveSessionState>({ status: 'loading' })
  const [finalDone, setFinalDone] = useState(false)
  const [nonce, setNonce] = useState(0)
  const { emit } = useSignalLog()
  const emitRef = useRef(emit)
  useEffect(() => {
    emitRef.current = emit
  }, [emit])
  const applyRef = useRef<((session: LiveSession) => void) | null>(null)

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let controller: AbortController | null = null
    let inFlight = false
    let failures = 0
    let held: LiveSession | null = null
    let phase: Phase = 'poll'

    const isHidden = () => document.visibilityState === 'hidden'
    const clearTimer = () => {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
    }
    const schedule = (ms: number) => {
      clearTimer()
      if (cancelled || phase === 'done' || isHidden()) return
      timer = setTimeout(read, ms)
    }

    function apply(session: LiveSession) {
      const wasActive = held !== null && isActive(held)
      held = session
      if (isActive(session)) phase = 'poll'
      else if (phase === 'final' || !wasActive) phase = 'done'
      else phase = 'final'
      setState({ status: 'ready', session, receivedAt: Date.now() })
      setFinalDone(phase === 'done')
      observeLiveSession(session, emitRef.current)
      mergeLiveSession(session)
      if (phase === 'poll')
        schedule(session.status === 'stopping' ? SESSION_STOPPING_POLL_MS : SESSION_POLL_MS)
      else {
        clearTimer()
        if (phase === 'final' && !isHidden() && !inFlight) read()
      }
    }
    applyRef.current = apply

    function read() {
      timer = null
      if (cancelled || inFlight || isHidden()) return
      inFlight = true
      const own = new AbortController()
      controller = own
      quantApi.getLiveSession(id, own.signal).then(
        (session) => {
          if (cancelled || own.signal.aborted) return
          inFlight = false
          failures = 0
          apply(session)
        },
        (error: unknown) => {
          if (cancelled || own.signal.aborted) return
          inFlight = false
          if (isLiveGone(error)) {
            phase = 'done'
            clearTimer()
            setState({ status: 'not_found' })
            return
          }
          failures += 1
          const message = quantErrorMessage(error)
          setState((prev) =>
            prev.status === 'ready' ? { ...prev, error: message } : { status: 'error', message },
          )
          schedule(nextDelay(failures))
        },
      )
    }

    function onVisibility() {
      if (cancelled) return
      if (isHidden()) {
        clearTimer()
        return
      }
      if (phase !== 'done' && !inFlight) {
        clearTimer()
        read()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    read()
    return () => {
      cancelled = true
      clearTimer()
      controller?.abort()
      applyRef.current = null
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [id, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  const merge = useCallback((session: LiveSession) => applyRef.current?.(session), [])
  const active = state.status === 'ready' && isActive(state.session)
  return { ...state, active, finalDone, reload, merge }
}
